import { useEffect, useState } from "react";
import { X, Twitter, Facebook, Linkedin, MessageCircle, Mail, Smartphone, Copy, Check, Share2, Image as ImageIcon, AtSign, Send, Hash } from "lucide-react";
import { fetchShareLinks } from "../lib/api";
import ShareCardModal from "./ShareCardModal";

export default function ShareModal({ article, onClose }) {
  const [links, setLinks] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showCard, setShowCard] = useState(false);

  useEffect(() => {
    if (!article) return;
    fetchShareLinks(article.id).then(setLinks).catch(() => setLinks({ copy: article.link }));
  }, [article]);

  // X (Twitter) is the primary share target per Newseze strategy; remaining
  // platforms are surfaced in priority order. Mail/SMS sit at the end.
  const items = [
    { key: "x", label: "X / Twitter", icon: Twitter, href: links?.x, primary: true },
    { key: "threads", label: "Threads", icon: AtSign, href: links?.threads },
    { key: "bluesky", label: "Bluesky", icon: Hash, href: links?.bluesky },
    { key: "facebook", label: "Facebook", icon: Facebook, href: links?.facebook },
    { key: "linkedin", label: "LinkedIn", icon: Linkedin, href: links?.linkedin },
    { key: "reddit", label: "Reddit", icon: MessageCircle, href: links?.reddit },
    { key: "telegram", label: "Telegram", icon: Send, href: links?.telegram },
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: links?.whatsapp },
    { key: "sms", label: "Text Message", icon: Smartphone, href: links?.sms },
    { key: "email", label: "Email", icon: Mail, href: links?.email },
  ];

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(links?.copy || article?.link || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  if (!article) return null;
  return (
    <div className="modal-back" onClick={onClose} data-testid="share-modal">
      <div className="modal !max-w-[520px]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.16em] font-black text-[var(--blue)]">
            <Share2 size={14} /> Send / Share
          </div>
          <button onClick={onClose} className="text-[var(--muted)] hover:text-[var(--ink)]" aria-label="Close" data-testid="share-close">
            <X size={20} />
          </button>
        </div>
        <h3 className="text-[18px] font-black leading-snug mb-4">{article.title}</h3>

        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
          {items.map((it) => (
            <a
              key={it.key}
              href={it.href || "#"}
              target={it.key === "sms" || it.key === "email" ? "_self" : "_blank"}
              rel="noopener noreferrer"
              className={`flex flex-col items-center gap-2 p-3 rounded-md transition ${
                it.primary
                  ? "border-2 border-[var(--ink)] bg-[var(--ink)] text-white hover:bg-black"
                  : "border border-[var(--line)] hover:bg-[var(--sky)] hover:border-[var(--sky-2)]"
              }`}
              data-testid={`share-${it.key}`}
            >
              <it.icon size={20} className={it.primary ? "text-white" : "text-[var(--blue)]"} />
              <span className={`text-[11px] font-bold text-center ${it.primary ? "text-white" : "text-[var(--ink)]"}`}>{it.label}</span>
            </a>
          ))}
        </div>

        <button onClick={() => setShowCard(true)} className="btn gold w-full justify-center mb-2" data-testid="share-card-open">
          <ImageIcon size={14} /> Make a TikTok / Instagram card
        </button>
        <button onClick={onCopy} className="btn w-full justify-center" data-testid="share-copy">
          {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy Newseze link</>}
        </button>
      </div>
      {showCard && <ShareCardModal articleId={article.id} onClose={() => setShowCard(false)} />}
    </div>
  );
}
